import * as React from 'react';

import {
  addCartLines,
  createCart,
  createShopifyStorefrontClient,
  fetchCart,
  normalizeCart,
  removeCartLines,
  updateCartLines,
} from '../lib/shopifyStorefront';

const client = createShopifyStorefrontClient();

const emptyCheckout = normalizeCart(null);

const defaultValues = {
  addToCart: () => {},
  checkout: emptyCheckout,
  clearCart: () => {},
  client,
  doCheckout: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
};

const isBrowser = typeof window !== `undefined`;
const localStorageKey = `shopify_cart_id`;
const legacyLocalStorageKey = `shopify_checkout_id`;

export const StoreContext = React.createContext(defaultValues);

export function StoreProvider({ children }) {
  const [checkout, setCheckout] = React.useState(emptyCheckout);

  function setCheckoutItem(nextCheckout) {
    if (isBrowser) {
      if (nextCheckout?.id) {
        localStorage.setItem(localStorageKey, nextCheckout.id);
      }
      localStorage.removeItem(legacyLocalStorageKey);
    }

    setCheckout(nextCheckout);
  }

  React.useEffect(() => {
    async function initializeCart() {
      const existingCartId = isBrowser ? localStorage.getItem(localStorageKey) : null;

      if (existingCartId && existingCartId !== `null`) {
        try {
          const existingCart = await fetchCart(client, existingCartId);
          if (existingCart?.id) {
            setCheckoutItem(existingCart);
            return;
          }
        } catch (error) {
          localStorage.removeItem(localStorageKey);
        }
      }

      if (isBrowser) {
        localStorage.removeItem(legacyLocalStorageKey);
      }

      try {
        const newCart = await createCart(client);
        setCheckoutItem(newCart);
      } catch (error) {
        console.error('initializeCart failed', error);
      }
    }

    initializeCart();
  }, []);

  async function addToCart(variantId, quantity) {
    const lines = [{ merchandiseId: variantId, quantity: Number(quantity) || 1 }];

    try {
      if (!checkout.id) {
        const newCart = await createCart(client, lines);
        setCheckoutItem(newCart);
        return;
      }

      const res = await addCartLines(client, checkout.id, lines);
      setCheckoutItem(res);
    } catch (error) {
      console.error('addToCart failed', error);
    }
  }

  async function removeFromCart(itemId) {
    if (!checkout.id) {
      return;
    }

    try {
      const res = await removeCartLines(client, checkout.id, [itemId]);
      setCheckoutItem(res);
    } catch (error) {
      console.error('removeFromCart failed', error);
    }
  }

  async function updateQuantity(itemId, quantity) {
    if (!checkout.id) {
      return;
    }

    try {
      const res = await updateCartLines(client, checkout.id, [{ id: itemId, quantity: Number(quantity) || 1 }]);
      setCheckoutItem(res);
    } catch (error) {
      console.error('updateQuantity failed', error);
    }
  }

  async function clearCart() {
    if (!checkout.id) {
      return;
    }

    const lineItemIds = checkout.lineItems.map((item) => item.id);
    if (!lineItemIds.length) {
      return;
    }

    try {
      const res = await removeCartLines(client, checkout.id, lineItemIds);
      setCheckoutItem(res);
    } catch (error) {
      console.error('clearCart failed', error);
    }
  }

  function doCheckout() {
    const checkoutUrl = checkout.checkoutUrl || checkout.webUrl;
    if (checkoutUrl) {
      window.open(checkoutUrl);
    }
  }

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addToCart,
        checkout,
        clearCart,
        doCheckout,
        removeFromCart,
        updateQuantity,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}
